import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type StyleProp,
    type ViewStyle,
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type IBubbleReactionsAddProps from "./type";
import type {
    BubbleReactionsAddKeyboardEventType,
    BubbleReactionsAddBlurEventType,
    BubbleReactionsAddWebPropsType
} from "./type";
import {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../../../core/hooks";
import {
    useInteractionState
} from "../../../../../../helpers/interaction";
import {
    MOTION_SURFACE_SCALE,
    getAppearStyle,
    usePresence
} from "../../../../../../helpers/motion";
import {
    usePointerType
} from "../../../../../../helpers/pointer";
import {
    SmilePlus as SmilePlusIcon
} from "lucide-react-native";
import {
    getFocusRingStyle
} from "../../../../../../helpers/theme/state";
import StateLayer from "../../../../../stateLayer";
import stylesheet from "../../stylesheet";
import Icon from "../../../../../icon";
import Text from "../../../../../text";

const BubbleReactionsAddItem: FC<{
    customTheme: IBubbleReactionsAddProps["customTheme"];
    onSelect: (emoji: string) => void;
    itemStyle: StyleProp<ViewStyle>;
    borderRadius: number;
    emoji: string;
}> = ({
    borderRadius,
    customTheme,
    itemStyle,
    onSelect,
    emoji
}) => {
    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
    });

    const {
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const handlePress = () => {
        onSelect(emoji);
    };

    return <Pressable
        {...interactionHandlers}
        accessibilityLabel={emoji}
        style={[
            itemStyle,
            getFocusRingStyle({
                isFocusVisible,
                isInset: true,
                borders,
                colors
            })
        ]}
        onPress={handlePress}
        aria-label={emoji}
        role="menuitem"
    >
        <StateLayer
            borderRadius={borderRadius}
            customTheme={customTheme}
            isHovered={isHovered}
            isPressed={isPressed}
            borderWidth={0}
        />
        <Text
            customTheme={customTheme}
            variant="titleMediumSize"
            aria-hidden={true}
        >
            {emoji}
        </Text>
    </Pressable>;
};

const BubbleReactionsAdd: FC<IBubbleReactionsAddProps> = ({
    customLocalize,
    quickReactions,
    customTheme,
    onSelect,
    align
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
    });

    const [
        isOpen,
        setIsOpen
    ] = useState<boolean>(false);

    const buttonRef = useRef<View>(null);

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        enterIntent: "surfaceEnter",
        exitIntent: "surfaceExit",
        isVisible: isOpen,
        customTheme
    });

    const appearStyle = useMemo(() => getAppearStyle({
        fromScale: MOTION_SURFACE_SCALE,
        isReduceMotionEnabled,
        progress
    }), [
        isReduceMotionEnabled,
        progress
    ]);

    const {
        surface: surfaceDynamicStyle,
        itemRadius: itemRadiusStyle,
        strip: stripDynamicStyle,
        item: itemDynamicStyle,
        add: addDynamicStyle
    } = useStyles({
        controlHeights,
        isFocusVisible,
        shapeConfigs,
        touchTargets,
        pointerType,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces,
        align
    });

    const getStrip = () => {
        if(Platform.OS !== "web") {
            return null;
        }

        const button = buttonRef.current as unknown as HTMLElement | null;

        return button?.parentElement?.querySelector<HTMLElement>("[role=\"menu\"]") ?? null;
    };

    const getElements = () => {
        return Array.from(getStrip()?.querySelectorAll<HTMLElement>("[role=\"menuitem\"]") ?? []);
    };

    const handleFocusButton = () => {
        if(Platform.OS === "web") {
            (buttonRef.current as unknown as HTMLElement | null)?.focus();
        }
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleSelect = (emoji: string) => {
        setIsOpen(false);

        onSelect(emoji);

        handleFocusButton();
    };

    const handleKeyDown = (event: BubbleReactionsAddKeyboardEventType) => {
        const items = getElements();

        const currentIndex = items.findIndex((item) => item === document.activeElement);

        const isRtl = direction === "rtl";

        const next = isRtl ? "ArrowLeft" : "ArrowRight";

        const previous = isRtl ? "ArrowRight" : "ArrowLeft";

        if(event.key === "Escape") {
            event.preventDefault();

            setIsOpen(false);

            handleFocusButton();

            return;
        }

        if(event.key === "Tab") {
            setIsOpen(false);

            return;
        }

        if(items.length === 0) {
            return;
        }

        const targets: Record<string, number> = {
            [previous]: currentIndex < 0 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length,
            [next]: (currentIndex + 1) % items.length,
            End: items.length - 1,
            Home: 0
        };

        const target = targets[event.key];

        if(target === undefined) {
            return;
        }

        event.preventDefault();

        items[target]?.focus();
    };

    const handleStripBlur = (event: BubbleReactionsAddBlurEventType) => {
        const strip = getStrip();

        const relatedTarget = event.relatedTarget as Node | null | undefined;

        if(relatedTarget && strip?.contains(relatedTarget)) {
            return;
        }

        if(relatedTarget && (buttonRef.current as unknown as HTMLElement | null)?.contains(relatedTarget)) {
            return;
        }

        setIsOpen(false);
    };

    useEffect(() => {
        if(!isOpen) {
            return;
        }

        getStrip()?.focus();
    }, [
        isOpen
    ]);

    const webStripProps: BubbleReactionsAddWebPropsType = Platform.OS === "web" ? {
        "aria-orientation": "horizontal",
        onKeyDown: handleKeyDown,
        onBlur: handleStripBlur
    } : {};

    const webButtonProps = Platform.OS === "web" ? {
        "aria-haspopup": "menu"
    } : {};

    const title = localize("add-reaction");

    const itemStyle = [
        stylesheet.item,
        itemDynamicStyle
    ];

    const renderStrip = () => {
        if(!isRendered) {
            return null;
        }

        return <Animated.View
            {...webStripProps}
            style={[
                stylesheet.strip,
                stripDynamicStyle,
                {
                    transformOrigin: align === "start" ? "left bottom" : "right bottom"
                },
                appearStyle
            ]}
            aria-label={localize("quick-reactions")}
            tabIndex={-1}
            role="menu"
        >
            {
                quickReactions.map((emoji) => <BubbleReactionsAddItem
                    borderRadius={itemRadiusStyle}
                    customTheme={customTheme}
                    onSelect={handleSelect}
                    itemStyle={itemStyle}
                    emoji={emoji}
                    key={emoji}
                />)
            }
        </Animated.View>;
    };

    return <>
        <Pressable
            {...webButtonProps}
            {...interactionHandlers}
            accessibilityLabel={title}
            aria-expanded={isOpen}
            onPress={handleToggle}
            style={[
                stylesheet.add,
                addDynamicStyle
            ]}
            aria-label={title}
            ref={buttonRef}
            role="button"
        >
            <View
                style={[
                    stylesheet.addSurface,
                    surfaceDynamicStyle
                ]}
            >
                <StateLayer
                    borderRadius={radiuses.chip}
                    borderWidth={borders.line}
                    customTheme={customTheme}
                    isHovered={isHovered}
                    isPressed={isPressed}
                />
                <Icon
                    customTheme={customTheme}
                    icon={SmilePlusIcon}
                    size="small"
                />
            </View>
        </Pressable>
        {renderStrip()}
    </>;
};
export default BubbleReactionsAdd;
